// MACHINERY V2 (Stroje) — Route layout (M2.0 foundation).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §2.2 / §8 M2.0.
// Mirror source: settings/v2/SettingsV2Layout.tsx (which itself mirrors
// midas/components/v3/MidasV3Layout.tsx) — root div + hoisted Radix provider +
// side-effect token-sheet import.
//
// This is the coexistence entry point for the whole v2 sub-tree:
//   • Owns the section-scoped root class
//     `machinery-v2-root p-<palette> mod-stroje`. ALL v2 tokens resolve only
//     under that root, so neither palette can leak into the live app and the
//     swap is a runtime class toggle driven by `machineryV2UiSlice`.
//   • Imports the section token sheet ONCE as a side effect (kept OUT of
//     src/index.scss so retiring v2 is one folder delete away).
//   • Hoists ONE Radix TooltipProvider to the layout root so fleet capability
//     badges, kebab menus and dialogs all share it. MIDAS shipped a P6 crash
//     hotfix precisely because a provider sat too deep in the tree — do not
//     re-introduce per-subtree providers below this point.
//
// PORTAL RULE — READ BEFORE ADDING THE FIRST TOOLTIP / MENU / DIALOG (M2.1+).
// Radix Tooltip.Content, DropdownMenu.Content, Popover.Content and Dialog.Content
// render through a portal to document.body, i.e. OUTSIDE `.machinery-v2-root`,
// where --card / --border / --ink / --shadow / --mod-accent DO NOT RESOLVE. A
// portalled node styled with those vars paints unthemed. Nothing portals in M2.0
// (no tooltip is rendered yet), so this is a guardrail, not a live bug.
// Countermeasure (ported from SettingsV2Layout.tsx:19-25, itself the
// ConfirmDialogV3 / NewChatDialogV3 pattern): portals ARE permitted to
// document.body PROVIDED the portalled node's own wrapper re-carries
// `machinery-v2-root p-${palette} mod-stroje` AND sets inline
// background:"transparent" — the transparent override defeats
// .machinery-v2-root's opaque var(--bg) paint (which would otherwise fill the
// screen behind an overlay) while still letting the scoped CSS vars resolve
// inside the portalled subtree. Read `palette` from `machineryV2UiSlice` at the
// portalling component, not from a captured constant. Non-portalled children
// need none of this.
// M2.1 note: the rule is LIVE, not theoretical, from here on — ChromeNavV4's
// right cluster mounts the first two real portals (user menu + language menu).
// Both are implemented in chrome/ChromeRightMenus.tsx, whose shared
// MenuContent wrapper is the single place the countermeasure lives.
//   • Does NOT render the global MachineID top bar: the shared app shell
//     renders above and this layout fills the remainder
//     (flex:1; height:100%; minHeight:0; overflow:hidden).
//   • ChromeNavV4 (M2.1) mounts INSIDE this root, above the <Outlet/> — it is
//     v2 PAGE chrome today and the PERMANENT platform nav from M2.12 (§9 D2),
//     so it belongs to the layout, not to any individual v2 page.
//
// Styling stack (ticket §3, decision D1 — locked): inline style objects for
// layout + the scoped CSS-var token sheet + Radix primitives consumed directly.
// No Tailwind, no shadcn, no MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";
import { Outlet } from "react-router-dom";
import * as RadixTooltip from "@radix-ui/react-tooltip";

import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import ChromeNavV4 from "./chrome/ChromeNavV4";

// Side-effect import — registers the v2 token sheet (scoped to
// .machinery-v2-root). Loaded ONCE here for the whole sub-tree.
import "./machinery-v2-tokens.scss";

const MachineryV2Layout: React.FC = () => {
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);

  return (
    <RadixTooltip.Provider delayDuration={200}>
      <div
        className={`machinery-v2-root p-${palette} mod-stroje`}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          // Background + ink come from .machinery-v2-root via the token sheet.
        }}
      >
        <ChromeNavV4 active="stroje" />
        <Outlet />
      </div>
    </RadixTooltip.Provider>
  );
};

export default MachineryV2Layout;
